import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { PictureAsPdf, VisibilityOutlined } from '@mui/icons-material';
import { RecipeV2Detail } from '../../../app/services/api/recipeV2';
import { loadGovernedPublishingRecipes } from '../publishing/loadPublishingRecipes';
import {
  RecipePublishStyle,
  saveAllergenMatrixPdf,
  saveRecipeBookPdf,
} from '../publishing/recipePublishing';
import GovernedMatrixPreview from './GovernedMatrixPreview';

interface RecipeCollectionPublishDialogProps {
  open: boolean;
  title: string;
  loadRecipeIds: () => Promise<number[]>;
  onClose: () => void;
}

const RecipeCollectionPublishDialog = ({
  open,
  title,
  loadRecipeIds,
  onClose,
}: RecipeCollectionPublishDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const [recipeIds, setRecipeIds] = useState<number[]>([]);
  const [style, setStyle] = useState<RecipePublishStyle>('print');
  const [output, setOutput] = useState<'book' | 'matrix'>('book');
  const [matrixPreview, setMatrixPreview] = useState<RecipeV2Detail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecipeIds([]);
      setMatrixPreview([]);
      setError(null);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    loadRecipeIds()
      .then((ids) => Array.from(new Set(ids)))
      .then((ids) => { if (active) setRecipeIds(ids); })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : t('publishing.loadError', 'Unable to load the governed recipes.'));
      })
      .finally(() => { if (active) setIsLoading(false); });

    return () => { active = false; };
  }, [open, loadRecipeIds, t]);

  const loadGovernedRecipes = () => loadGovernedPublishingRecipes(recipeIds);

  const handlePreviewMatrix = async () => {
    if (!recipeIds.length) return;
    setIsPreviewLoading(true);
    setError(null);
    try {
      setMatrixPreview(await loadGovernedRecipes());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.loadError', 'Unable to load the governed recipes.'));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!recipeIds.length) return;
    setIsGenerating(true);
    setError(null);
    try {
      const governedRecipes = matrixPreview.length
        ? matrixPreview
        : await loadGovernedRecipes();
      if (output === 'matrix') {
        saveAllergenMatrixPdf(governedRecipes, {
          style,
          title: `${title} - Allergen & Dietary Matrix`,
          fileNamePrefix: `${title}-matrix`,
        });
      } else {
        await saveRecipeBookPdf(governedRecipes, {
          style,
          title,
          fileNamePrefix: title,
        });
      }
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.error', 'Unable to generate the PDF.'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open}
      onClose={isGenerating ? undefined : onClose}
      maxWidth={output === 'matrix' && matrixPreview.length ? 'xl' : 'sm'}
      fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography color="text.secondary">
            {t('publishing.collectionDescription', 'Generate a cookbook or inspect/export an allergen and dietary matrix from the governed recipes assigned to this collection. Costing and other commercial information are excluded.')}
          </Typography>

          <RadioGroup row
            value={output}
            onChange={(event) => {
              setOutput(event.target.value as 'book' | 'matrix');
              setMatrixPreview([]);
            }}>
            <FormControlLabel value="book"
              control={<Radio />}
              label={t('publishing.recipeBook', 'Recipe book')} />
            <FormControlLabel value="matrix"
              control={<Radio />}
              label={t('publishing.allergenMatrix', 'Allergen & dietary matrix')} />
          </RadioGroup>

          <RadioGroup
            value={style}
            onChange={(event) => setStyle(event.target.value as RecipePublishStyle)}
          >
            <FormControlLabel value="print"
              control={<Radio />}
              label={t('publishing.printFriendly', 'Print-friendly - minimal ink, no decorative imagery')} />
            <FormControlLabel value="full"
              control={<Radio />}
              label={t('publishing.fullColour', 'Full-colour - richer presentation with imagery where available')} />
          </RadioGroup>

          {isLoading && <Typography color="text.secondary">{t('loading')}</Typography>}
          {!isLoading && !error && (
            <Typography variant="textSM">
              {t('publishing.recipesIncluded', '{{count}} governed recipes included', { count: recipeIds.length })}
            </Typography>
          )}
          {error && <Typography color="error">{error}</Typography>}

          {output === 'matrix' && (
            <>
              <Button
                variant="secondary"
                startIcon={<VisibilityOutlined />}
                disabled={isLoading || isPreviewLoading || recipeIds.length === 0}
                onClick={() => { void handlePreviewMatrix(); }}
              >
                {isPreviewLoading
                  ? t('publishing.loadingPreview', 'Loading preview...')
                  : t('publishing.previewMatrix', 'Preview matrix')}
              </Button>
              {matrixPreview.length > 0 && <GovernedMatrixPreview recipes={matrixPreview} />}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}
          disabled={isGenerating}>{t('actions.cancel', 'Cancel')}</Button>
        <Button
          variant="primary"
          startIcon={<PictureAsPdf />}
          disabled={isLoading || isGenerating || recipeIds.length === 0}
          onClick={() => { void handleGenerate(); }}
        >
          {isGenerating
            ? t('publishing.generating', 'Generating...')
            : t('publishing.downloadPdf', 'Download PDF')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeCollectionPublishDialog;
